import React, { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const OnboardingPending: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Real-time listener for KYC status changes
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔍 Setting up KYC status listener for user:', user.id);
    
    const userDocRef = doc(db, 'users', user.id);
    
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        const kycStatus = userData.kyc?.status;
        
        console.log('📊 KYC status update:', kycStatus);
        
        // If KYC is approved, redirect to dashboard
        if (kycStatus === 'approved') {
          console.log('✅ KYC approved! Redirecting to dashboard...');
          navigate('/dashboard', { replace: true });
        }
        // If KYC is rejected, redirect to KYC resubmission
        else if (kycStatus === 'rejected') {
          console.log('❌ KYC rejected! Redirecting to KYC resubmission...');
          navigate('/onboarding/kyc', { replace: true });
        }
      }
    }, (error) => {
      console.error('❌ Error listening to KYC status:', error);
    });

    return () => {
      console.log('🔕 Cleaning up KYC status listener');
      unsubscribe();
    };
  }, [user?.id, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center text-center px-6 py-16">
      <div className="w-full max-w-md mb-6 flex justify-start px-4 sm:px-0">
        <button
          onClick={handleLogout}
          className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1 sm:gap-2 px-2 sm:px-0 py-1 sm:py-0"
        >
          <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-xs sm:text-sm font-medium">Logout</span>
        </button>
      </div>
      <div className="w-16 h-16 rounded-full bg-[#ee4238] text-white flex items-center justify-center mb-6">
        <span className="text-2xl font-black">✓</span>
      </div>
      <h1 className="text-2xl font-black text-gray-900">Thank you for submitting your details</h1>
      <p className="mt-3 text-gray-700">
        Your KYC and bank details have been submitted successfully. We will review your documents shortly.
      </p>
      <div className="mt-8 w-full max-w-md text-left bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">KYC Status</span>
          <span className="text-xs font-bold px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
            {user?.kyc?.status === 'approved' ? 'Approved' : 'Pending'}
          </span>
        </div>
        <p className="mt-2 text-sm text-gray-600">You will get access to app features once your KYC is approved.</p>
      </div>

      <p className="mt-8 text-sm text-gray-600">You can close the app now and check back later.</p>
    </div>
  );
};

export default OnboardingPending;


